import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    collection, query, where, limit, getDocs,
    doc, updateDoc, writeBatch, Timestamp, deleteField,
} from 'firebase/firestore';
import {
    X, Save, Star, Plus, Trash2, BellRing, UserRound, Baby, CalendarClock,
    History, Heart, Search, ShoppingCart, RotateCw,
} from 'lucide-react';
import { auth, db } from './firebase';
import { toLocalPhone } from './CallHistory';

/**
 * ─────────────────────────────────────────────────────────────
 *  Customer Marketing Profile + Repeat Order Reminder
 *
 *  সবকিছু order document এর ভেতরেই সেভ হয় (নতুন collection লাগে না):
 *    orders/{id}.customerProfile  ← high ticket, profession, কেন নিয়েছে, বাচ্চার বয়স, ...
 *    orders/{id}.repeatReminder   ← { status: 'pending'|'done', date: 'YYYY-MM-DD', product, note }
 *
 *  Delivered Orders পেজ আর Dashboard — দুই জায়গা থেকেই এই modal খোলা যায়।
 * ─────────────────────────────────────────────────────────────
 */

// ─── Shared helpers (Dashboard / Delivered page ও ব্যবহার করে) ───────────────
// কাস্টমার কতবার order নিয়েছেন (Delivered) তা দেখে category suggestion — শুধু suggestion, আসল category আপনি নিজে বাছবেন
export const TIER_BY_ORDERS = { high: 5, medium: 3, regular: 2 }; // এত বা এর বেশি বার order হলে

export const TIERS = [
    { value: 'high', label: 'High Ticket', hint: 'বড় amount / VIP', icon: '⭐', badge: 'bg-amber-400 text-white', soft: 'bg-amber-100 text-amber-700', ring: 'border-amber-400 bg-amber-50' },
    { value: 'medium', label: 'Medium', hint: 'মাঝারি কাস্টমার', icon: '🔷', badge: 'bg-blue-500 text-white', soft: 'bg-blue-100 text-blue-700', ring: 'border-blue-400 bg-blue-50' },
    { value: 'regular', label: 'Regular', hint: 'সাধারণ কাস্টমার', icon: '🟢', badge: 'bg-emerald-500 text-white', soft: 'bg-emerald-100 text-emerald-700', ring: 'border-emerald-400 bg-emerald-50' },
    { value: 'low', label: 'Lower Class', hint: 'কম amount / কম বাজেট', icon: '⚪', badge: 'bg-gray-500 text-white', soft: 'bg-gray-200 text-gray-700', ring: 'border-gray-400 bg-gray-100' },
];
export const tierCfg = (v) => TIERS.find((t) => t.value === v) || null;
/** পুরনো data (শুধু isHighTicket ছিল) ও ধরে */
export const tierOf = (profile) => profile?.tier || (profile?.isHighTicket ? 'high' : '');
export const suggestTier = (orderCount) => {
    const n = Number(orderCount || 0);
    if (n >= TIER_BY_ORDERS.high) return 'high';
    if (n >= TIER_BY_ORDERS.medium) return 'medium';
    if (n >= TIER_BY_ORDERS.regular) return 'regular';
    return 'low';
};

export function TierBadge({ tier }) {
    const t = tierCfg(tier);
    if (!t) return null;
    return (
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${t.badge}`}>
            {t.icon} {t.label}
        </span>
    );
}

// ─── Create Order এ সরাসরি পাঠানো (customer তথ্য + product আগে থেকে সিলেক্ট) ─────
/** পছন্দের product থাকলে সেগুলো, না থাকলে ওই order এর নিজের product */
export const buildPrefill = (order, productIds) => {
    const pref = order.customerProfile?.preferredProducts || [];
    const ids = productIds
        || (pref.length ? pref.map((x) => x.productId) : (order.cartItems || []).map((x) => x.productId));
    return {
        name: order.customerInfo?.name || '',
        phone: order.customerInfo?.phone || '',
        address: order.customerInfo?.address || '',
        notes: '',
        productIds: ids.filter(Boolean),
    };
};
export const openCreateOrder = (navigate, order, productIds) => {
    navigate('/create-order', { state: { prefill: buildPrefill(order, productIds) } });
};

// কাস্টমারের সব profile-করা order এ একই রাখা হয় এমন field (বাকিগুলো শুধু ওই order এর)
const CUSTOMER_LEVEL_FIELDS = [
    'tier', 'isHighTicket', 'profession', 'professionOther', 'gender', 'ageGroup',
    'budget', 'source', 'contact', 'bestTime', 'interests', 'nextInterest', 'preferredProducts',
];

const STATUS_STYLE = {
    Delivered: { label: 'Delivered', cls: 'bg-green-100 text-green-700' },
    Return: { label: 'Return', cls: 'bg-purple-100 text-purple-700' },
    Cancel: { label: 'Cancel', cls: 'bg-red-100 text-red-700' },
};
const statusStyle = (st) => STATUS_STYLE[st] || { label: st || 'Unknown', cls: 'bg-gray-100 text-gray-600' };
const orderDate = (ts) => {
    try {
        const d = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
        return d ? `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}` : '';
    } catch (e) { return ''; }
};

const pad = (n) => String(n).padStart(2, '0');
export const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseYMD = (s) => {
    const [y, m, d] = String(s || '').split('-').map(Number);
    return y ? new Date(y, m - 1, d) : null;
};
/** আজ থেকে কত দিন বাকি (নেগেটিভ = দেরি হয়ে গেছে) */
export const daysFromToday = (ymd) => {
    const t = parseYMD(ymd);
    if (!t) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((t - today) / 86400000);
};
export const fmtYMD = (ymd) => {
    const t = parseYMD(ymd);
    return t ? `${t.getDate()}/${t.getMonth() + 1}/${t.getFullYear()}` : '';
};
export const addDaysYMD = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return toYMD(d);
};
export const addMonthsYMD = (months) => {
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return toYMD(d);
};

export const PROFESSIONS = [
    'ছাত্র/ছাত্রী', 'চাকরিজীবী', 'ব্যবসায়ী', 'ডাক্তার', 'শিক্ষক', 'ইঞ্জিনিয়ার',
    'গৃহিণী', 'ফ্রিল্যান্সার', 'কৃষক', 'ড্রাইভার', 'প্রবাসী', 'অন্যান্য',
];

export const PURPOSES = [
    { value: 'self', label: 'নিজের ব্যবহার' },
    { value: 'child', label: 'বাচ্চার জন্য' },
    { value: 'gift', label: 'উপহার (Gift)' },
    { value: 'family', label: 'পরিবারের জন্য' },
    { value: 'business', label: 'ব্যবসা / Resell' },
    { value: 'office', label: 'অফিস / কাজের জন্য' },
    { value: 'other', label: 'অন্যান্য' },
];
export const purposeLabel = (v) => PURPOSES.find((p) => p.value === v)?.label || v;

const AGE_GROUPS = ['১৮ এর নিচে', '১৮-২৪', '২৫-৩৪', '৩৫-৪৪', '৪৫-৫৪', '৫৫+'];
const BUDGETS = [
    { value: 'low', label: 'কম বাজেট' },
    { value: 'medium', label: 'মাঝারি' },
    { value: 'high', label: 'উচ্চ' },
    { value: 'premium', label: 'Premium' },
];
const SOURCES = [
    { value: 'facebook_ad', label: 'Facebook Ad' },
    { value: 'facebook_page', label: 'Facebook Page' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'google', label: 'Google' },
    { value: 'friend', label: 'বন্ধু / রেফারেন্স' },
    { value: 'repeat', label: 'আগের কাস্টমার' },
    { value: 'other', label: 'অন্যান্য' },
];
const SATISFACTION = [
    { value: 'happy', label: '😊 খুশি' },
    { value: 'okay', label: '😐 মোটামুটি' },
    { value: 'unhappy', label: '😞 অখুশি' },
];
const CONTACTS = [
    { value: 'call', label: 'কল' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'messenger', label: 'Messenger' },
];
const QUICK_DATES = [
    { label: '১৫ দিন', get: () => addDaysYMD(15) },
    { label: '১ মাস', get: () => addMonthsYMD(1) },
    { label: '২ মাস', get: () => addMonthsYMD(2) },
    { label: '৩ মাস', get: () => addMonthsYMD(3) },
    { label: '৬ মাস', get: () => addMonthsYMD(6) },
    { label: '১ বছর', get: () => addMonthsYMD(12) },
];

const inputCls = 'w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400';
const labelCls = 'block text-sm font-medium text-gray-700 mb-1.5';

function Chip({ active, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition
                ${active ? 'bg-orange-400 border-orange-400 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-orange-50'}`}
        >
            {children}
        </button>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <div className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3 text-gray-900 font-semibold">
                <Icon size={18} className="text-orange-500" />
                {title}
            </div>
            <div className="space-y-4">{children}</div>
        </div>
    );
}

export default function CustomerProfileModal({ order, onClose, onSaved }) {
    const navigate = useNavigate();
    const p0 = order.customerProfile || {};
    const r0 = order.repeatReminder || null;
    const productNames = (order.cartItems || []).map((it) => it.name).filter(Boolean).join(', ');

    const [tier, setTier] = useState(tierOf(p0));
    const [profession, setProfession] = useState(p0.profession || '');
    const [professionOther, setProfessionOther] = useState(p0.professionOther || '');
    const [gender, setGender] = useState(p0.gender || '');
    const [ageGroup, setAgeGroup] = useState(p0.ageGroup || '');
    const [budget, setBudget] = useState(p0.budget || '');
    const [purposes, setPurposes] = useState(p0.purposes || []);
    const [purposeNote, setPurposeNote] = useState(p0.purposeNote || '');
    const [children, setChildren] = useState(p0.children || []);
    const [occasion, setOccasion] = useState(p0.occasion || '');
    const [source, setSource] = useState(p0.source || '');
    const [satisfaction, setSatisfaction] = useState(p0.satisfaction || '');
    const [contact, setContact] = useState(p0.contact || '');
    const [bestTime, setBestTime] = useState(p0.bestTime || '');
    const [interests, setInterests] = useState(p0.interests || '');
    const [nextInterest, setNextInterest] = useState(p0.nextInterest || '');
    const [notes, setNotes] = useState(p0.notes || '');

    const [repeatOn, setRepeatOn] = useState(!!r0);
    const [repeatDate, setRepeatDate] = useState(r0?.date || '');
    const [repeatProduct, setRepeatProduct] = useState(r0?.product ?? productNames);
    const [repeatNote, setRepeatNote] = useState(r0?.note || '');

    const [prefilled, setPrefilled] = useState(false);
    const [orderCount, setOrderCount] = useState(null); // এই নম্বরের মোট Delivered order
    const [history, setHistory] = useState(null);        // এই কাস্টমারের সব order (Delivered / Return / Cancel / ...)
    const [preferred, setPreferred] = useState(p0.preferredProducts || []);
    const [allProducts, setAllProducts] = useState([]);
    const [productSearch, setProductSearch] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const isFirstProfile = !order.customerProfile;
    const phone = order.customerInfo?.phone;

    // এই ফোন নম্বরের সব order পড়ে: (১) কতবার Delivered order নিয়েছেন গুনে category suggestion,
    // (২) আগের কোনো order এ profile থাকলে customer-level তথ্য আগে থেকে ভরে দেয়
    useEffect(() => {
        if (!phone) { setOrderCount(0); setHistory([]); return; }
        let cancelled = false;
        (async () => {
            try {
                // 01712345678 / 8801712345678 / +8801712345678 / 1712345678 — সবই একই কাস্টমার
                const local = toLocalPhone(phone);
                const variants = local.length === 11
                    ? [local, `88${local}`, `+88${local}`, local.slice(1), `0088${local}`, `+${`88${local}`}`]
                    : [phone];
                const rawSnap = await getDocs(query(
                    collection(db, 'orders'),
                    where('customerInfo.phone', 'in', [...new Set(variants)]),
                    limit(200),
                ));
                if (cancelled) return;
                const docs = local.length === 11
                    ? rawSnap.docs.filter((d) => toLocalPhone(d.data().customerInfo?.phone) === local)
                    : rawSnap.docs;
                const snap = { docs };
                setOrderCount(docs.filter((d) => d.data().status === 'Delivered').length);
                setHistory(docs.map((d) => {
                    const x = d.data();
                    return {
                        id: d.id, orderId: x.orderId, status: x.status, created_at: x.created_at,
                        totalAmount: x.totalAmount, cartItems: x.cartItems || [], customerProfile: x.customerProfile || null,
                    };
                }).sort((a, b) => (b.created_at?.toMillis?.() || 0) - (a.created_at?.toMillis?.() || 0)));

                // অন্য order এ সেভ করা পছন্দের product গুলোও একসাথে দেখায়
                const merged = new Map();
                docs.forEach((d) => (d.data().customerProfile?.preferredProducts || []).forEach((pp) => {
                    if (pp?.productId && !merged.has(pp.productId)) merged.set(pp.productId, pp);
                }));
                if (merged.size) {
                    setPreferred((cur) => {
                        const m = new Map(merged);
                        cur.forEach((pp) => m.set(pp.productId, pp));
                        return [...m.values()];
                    });
                }

                if (!isFirstProfile) return;
                const prev = snap.docs
                    .filter((d) => d.id !== order.id && d.data().customerProfile)
                    .map((d) => d.data().customerProfile)
                    .sort((a, b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0))[0];
                if (!prev) return;
                setTier(tierOf(prev));
                setProfession(prev.profession || '');
                setProfessionOther(prev.professionOther || '');
                setGender(prev.gender || '');
                setAgeGroup(prev.ageGroup || '');
                setBudget(prev.budget || '');
                setSource(prev.source || '');
                setContact(prev.contact || '');
                setBestTime(prev.bestTime || '');
                setInterests(prev.interests || '');
                setPrefilled(true);
            } catch (e) {
                console.error('Customer order history failed:', e);
                if (!cancelled) { setOrderCount(0); setHistory([]); }
            }
        })();
        return () => { cancelled = true; };
    }, [isFirstProfile, phone, order.id]);

    // পছন্দের product বাছতে সব product লোড
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDocs(collection(db, 'products'));
                if (cancelled) return;
                setAllProducts(snap.docs.map((d) => {
                    const x = d.data();
                    return { productId: x.productId || d.id, name: x.name || '', image: x.image || '', price: x.price || 0, stock: x.stock || 0 };
                }).filter((x) => x.name));
            } catch (e) {
                console.error('Products load failed:', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const addPreferred = (pr) => {
        setPreferred((cur) => (cur.some((x) => x.productId === pr.productId)
            ? cur
            : [...cur, { productId: pr.productId, name: pr.name, image: pr.image, price: pr.price }]));
    };
    const removePreferred = (id) => setPreferred((cur) => cur.filter((x) => x.productId !== id));

    const searchResults = productSearch.trim()
        ? allProducts
            .filter((pr) => pr.name.toLowerCase().includes(productSearch.trim().toLowerCase()) && !preferred.some((x) => x.productId === pr.productId))
            .slice(0, 8)
        : [];

    const togglePurpose = (v) => {
        setPurposes((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
    };

    const updateChild = (i, patch) => setChildren((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

    const save = async (opts = {}) => {
        setError('');
        if (repeatOn && !repeatDate) return setError('Repeat order এর তারিখ দিন (অথবা Repeat বন্ধ করুন)');
        if (purposes.includes('child') && children.some((c) => c.age === '' || c.age === undefined)) {
            return setError('বাচ্চার বয়স লিখুন (অথবা খালি লাইনটি মুছে দিন)');
        }

        setBusy(true);
        try {
            const now = Timestamp.now();
            const customerProfile = {
                tier,
                isHighTicket: tier === 'high', // Dashboard query এর জন্য
                profession,
                professionOther: profession === 'অন্যান্য' ? professionOther.trim() : '',
                gender,
                ageGroup,
                budget,
                purposes,
                purposeNote: purposeNote.trim(),
                children: purposes.includes('child')
                    ? children.map((c) => ({ age: Number(c.age), unit: c.unit || 'year', gender: c.gender || '' }))
                    : [],
                occasion: occasion.trim(),
                source,
                satisfaction,
                contact,
                bestTime: bestTime.trim(),
                interests: interests.trim(),
                nextInterest: nextInterest.trim(),
                preferredProducts: preferred.map((x) => ({ productId: x.productId, name: x.name, image: x.image || '', price: x.price || 0 })),
                notes: notes.trim(),
                updatedAt: now,
                updatedBy: auth.currentUser?.email || '',
            };

            let repeatReminder = null;
            if (repeatOn) {
                const sameDateDone = r0?.status === 'done' && r0?.date === repeatDate;
                repeatReminder = {
                    status: sameDateDone ? 'done' : 'pending',
                    date: repeatDate,
                    product: repeatProduct.trim(),
                    note: repeatNote.trim(),
                    createdAt: r0?.createdAt || now,
                    updatedAt: now,
                    ...(sameDateDone && r0?.doneAt ? { doneAt: r0.doneAt } : {}),
                };
            }

            await updateDoc(doc(db, 'orders', order.id), {
                customerProfile,
                repeatReminder: repeatReminder || deleteField(),
            });

            // কাস্টমারের অন্য profile-করা order গুলোতেও customer-level তথ্য (category, পেশা, পছন্দের product...) একই রাখে
            try {
                const others = (history || []).filter((h) => h.id !== order.id && h.customerProfile);
                if (others.length) {
                    const fields = {};
                    CUSTOMER_LEVEL_FIELDS.forEach((k) => { fields[`customerProfile.${k}`] = customerProfile[k]; });
                    const batch = writeBatch(db);
                    others.slice(0, 400).forEach((h) => batch.update(doc(db, 'orders', h.id), fields));
                    await batch.commit();
                }
            } catch (syncErr) {
                console.error('Sync to other orders failed:', syncErr);
            }

            onSaved && onSaved(order.id, { customerProfile, repeatReminder });
            onClose();
            if (opts.create) {
                openCreateOrder(navigate, { ...order, customerProfile }, opts.productIds);
            }
        } catch (e) {
            console.error('Error saving customer profile:', e);
            setError(e.message || 'সেভ করা যায়নি');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl w-full max-w-2xl border border-gray-200 max-h-[92vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-start p-6 pb-3 flex-shrink-0">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Customer Profile</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            <span className="font-semibold text-gray-800">{order.customerInfo?.name || 'N/A'}</span>
                            {' · '}{order.customerInfo?.phone || 'N/A'}
                            {' · '}৳{order.totalAmount || 0}
                        </p>
                        {productNames && <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">{productNames}</p>}
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-900"><X size={24} /></button>
                </div>

                {/* Body */}
                <div className="px-6 pb-2 overflow-y-auto space-y-4">
                    {prefilled && (
                        <div className="p-3 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-lg">
                            এই নম্বরের আগের order থেকে কিছু তথ্য আগে থেকে ভরা হয়েছে — দেখে নিন, দরকার হলে বদলান।
                        </div>
                    )}

                    {/* Customer category */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">Customer Category</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {TIERS.map((t) => (
                                <button
                                    type="button"
                                    key={t.value}
                                    onClick={() => setTier(tier === t.value ? '' : t.value)}
                                    className={`p-3 rounded-xl border text-left transition ${tier === t.value ? t.ring : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                                >
                                    <div className="text-lg leading-none">{t.icon}</div>
                                    <div className="text-sm font-semibold text-gray-900 mt-1">{t.label}</div>
                                    <div className="text-xs text-gray-500">{t.hint}</div>
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5">
                            {orderCount === null ? 'Order history দেখা হচ্ছে...' : (
                                <>
                                    এই কাস্টমার এ পর্যন্ত <span className="font-bold text-gray-800">{orderCount} বার</span> Delivered order নিয়েছেন
                                    {orderCount > 0 && (
                                        <> — suggestion: <span className="font-semibold">{tierCfg(suggestTier(orderCount)).label}</span>
                                            {tier !== suggestTier(orderCount) && (
                                                <>{' '}<button type="button" onClick={() => setTier(suggestTier(orderCount))} className="text-orange-600 font-semibold underline">এটাই বাছুন</button></>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </p>
                    </div>

                    {/* Order history */}
                    <Section icon={History} title="এই কাস্টমারের Order History">
                        {history === null ? (
                            <p className="text-sm text-gray-500">Order history দেখা হচ্ছে...</p>
                        ) : history.length === 0 ? (
                            <p className="text-sm text-gray-500">এই নম্বরের কোনো order পাওয়া যায়নি</p>
                        ) : (
                            <>
                                <div className="flex flex-wrap gap-2">
                                    {['Delivered', 'Return', 'Cancel'].map((st) => (
                                        <span key={st} className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusStyle(st).cls}`}>
                                            {st}: {history.filter((h) => h.status === st).length}
                                        </span>
                                    ))}
                                    {history.some((h) => !STATUS_STYLE[h.status]) && (
                                        <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-gray-100 text-gray-600">
                                            অন্যান্য: {history.filter((h) => !STATUS_STYLE[h.status]).length}
                                        </span>
                                    )}
                                    <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-gray-900 text-white">মোট: {history.length}</span>
                                </div>
                                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                                    {history.slice(0, 15).map((h) => {
                                        const st = statusStyle(h.status);
                                        const names = (h.cartItems || []).map((x) => x.name).filter(Boolean).join(', ');
                                        return (
                                            <div key={h.id} className={`flex items-center gap-2 p-2.5 rounded-lg border ${h.id === order.id ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${st.cls}`}>{st.label}</span>
                                                        <span className="text-xs text-gray-500">{orderDate(h.created_at)}</span>
                                                        <span className="text-xs font-semibold text-gray-700">৳{h.totalAmount || 0}</span>
                                                        {h.id === order.id && <span className="text-xs text-orange-600 font-semibold">(এই order)</span>}
                                                    </div>
                                                    {names && <p className="text-xs text-gray-600 truncate mt-0.5">{names}</p>}
                                                </div>
                                                {(h.cartItems || []).length > 0 && (
                                                    <button
                                                        type="button"
                                                        disabled={busy}
                                                        onClick={() => save({ create: true, productIds: h.cartItems.map((x) => x.productId) })}
                                                        title="এই product গুলো দিয়ে আবার Order Create"
                                                        className="flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-lg bg-orange-100 text-orange-700 hover:bg-orange-200 disabled:opacity-50"
                                                    >
                                                        <RotateCw size={12} /> Reorder
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </Section>

                    {/* Customer */}
                    <Section icon={UserRound} title="Customer সম্পর্কে">
                        <div>
                            <label className={labelCls}>Profession (পেশা)</label>
                            <div className="flex flex-wrap gap-2">
                                {PROFESSIONS.map((pr) => (
                                    <Chip key={pr} active={profession === pr} onClick={() => setProfession(profession === pr ? '' : pr)}>{pr}</Chip>
                                ))}
                            </div>
                            {profession === 'অন্যান্য' && (
                                <input
                                    value={professionOther}
                                    onChange={(e) => setProfessionOther(e.target.value)}
                                    placeholder="কী পেশা লিখুন..."
                                    className={`${inputCls} mt-2`}
                                />
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className={labelCls}>Gender</label>
                                <select value={gender} onChange={(e) => setGender(e.target.value)} className={inputCls}>
                                    <option value="">Select</option>
                                    <option value="male">পুরুষ</option>
                                    <option value="female">মহিলা</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>বয়স (আনুমানিক)</label>
                                <select value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} className={inputCls}>
                                    <option value="">Select</option>
                                    {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>Budget level</label>
                                <select value={budget} onChange={(e) => setBudget(e.target.value)} className={inputCls}>
                                    <option value="">Select</option>
                                    {BUDGETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                                </select>
                            </div>
                        </div>
                    </Section>

                    {/* Purpose */}
                    <Section icon={Baby} title="Product কী কাজের জন্য নিয়েছেন / নিচ্ছেন">
                        <div className="flex flex-wrap gap-2">
                            {PURPOSES.map((pu) => (
                                <Chip key={pu.value} active={purposes.includes(pu.value)} onClick={() => togglePurpose(pu.value)}>{pu.label}</Chip>
                            ))}
                        </div>

                        {purposes.includes('child') && (
                            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg space-y-2">
                                <label className="block text-sm font-medium text-orange-800">বাচ্চার বয়স</label>
                                {children.length === 0 && <p className="text-xs text-orange-700">নিচের বাটন থেকে বাচ্চা যোগ করুন</p>}
                                {children.map((c, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="0"
                                            value={c.age ?? ''}
                                            onChange={(e) => updateChild(i, { age: e.target.value })}
                                            placeholder="বয়স"
                                            className={`${inputCls} w-24`}
                                        />
                                        <select value={c.unit || 'year'} onChange={(e) => updateChild(i, { unit: e.target.value })} className={`${inputCls} w-28`}>
                                            <option value="year">বছর</option>
                                            <option value="month">মাস</option>
                                        </select>
                                        <select value={c.gender || ''} onChange={(e) => updateChild(i, { gender: e.target.value })} className={`${inputCls} w-28`}>
                                            <option value="">ছেলে/মেয়ে</option>
                                            <option value="boy">ছেলে</option>
                                            <option value="girl">মেয়ে</option>
                                        </select>
                                        <button type="button" onClick={() => setChildren(children.filter((_, idx) => idx !== i))} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setChildren([...children, { age: '', unit: 'year', gender: '' }])}
                                    className="flex items-center gap-1 text-sm font-semibold text-orange-700 hover:text-orange-800"
                                >
                                    <Plus size={16} /> বাচ্চা যোগ করুন
                                </button>
                            </div>
                        )}

                        <div>
                            <label className={labelCls}>আরও বিস্তারিত (কী কাজে ব্যবহার করবেন)</label>
                            <input value={purposeNote} onChange={(e) => setPurposeNote(e.target.value)} placeholder="যেমন: বাচ্চার স্কুলের জন্য, ভাইয়ের বিয়েতে গিফট..." className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Occasion / বিশেষ উপলক্ষ</label>
                            <input value={occasion} onChange={(e) => setOccasion(e.target.value)} placeholder="যেমন: জন্মদিন, ঈদ, বিয়ে, নতুন বাসা..." className={inputCls} />
                        </div>
                    </Section>

                    {/* Marketing extras */}
                    <Section icon={Star} title="Marketing এর জন্য অতিরিক্ত তথ্য">
                        <div>
                            <label className={labelCls}>আমাদের কীভাবে চিনলেন?</label>
                            <div className="flex flex-wrap gap-2">
                                {SOURCES.map((s) => (
                                    <Chip key={s.value} active={source === s.value} onClick={() => setSource(source === s.value ? '' : s.value)}>{s.label}</Chip>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className={labelCls}>Product নিয়ে সন্তুষ্টি</label>
                            <div className="flex flex-wrap gap-2">
                                {SATISFACTION.map((s) => (
                                    <Chip key={s.value} active={satisfaction === s.value} onClick={() => setSatisfaction(satisfaction === s.value ? '' : s.value)}>{s.label}</Chip>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>যোগাযোগের পছন্দ</label>
                                <div className="flex flex-wrap gap-2">
                                    {CONTACTS.map((c) => (
                                        <Chip key={c.value} active={contact === c.value} onClick={() => setContact(contact === c.value ? '' : c.value)}>{c.label}</Chip>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>কখন কল করলে ভালো</label>
                                <input value={bestTime} onChange={(e) => setBestTime(e.target.value)} placeholder="যেমন: রাত ৮টার পর" className={inputCls} />
                            </div>
                        </div>
                        <div>
                            <label className={labelCls}>শখ / আগ্রহ (Interest)</label>
                            <input value={interests} onChange={(e) => setInterests(e.target.value)} placeholder="যেমন: রান্না, ফিটনেস, গেমিং..." className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>পরে কোন product এ আগ্রহী হতে পারেন</label>
                            <input value={nextInterest} onChange={(e) => setNextInterest(e.target.value)} placeholder="যেমন: কিচেন গ্যাজেট, বাচ্চাদের খেলনা..." className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>অন্যান্য নোট</label>
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="যা যা মনে রাখা দরকার..." className={`${inputCls} h-20 resize-none`} />
                        </div>
                    </Section>

                    {/* Preferred products */}
                    <Section icon={Heart} title="কাস্টমারের পছন্দের Product (পরে Marketing এর জন্য)">
                        {preferred.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {preferred.map((x) => (
                                    <span key={x.productId} className="flex items-center gap-2 pl-1.5 pr-2 py-1 bg-pink-50 border border-pink-200 rounded-full text-sm text-pink-800">
                                        {x.image && <img src={x.image} alt="" className="w-6 h-6 rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
                                        <span className="max-w-[180px] truncate">{x.name}</span>
                                        <button type="button" onClick={() => removePreferred(x.productId)} className="text-pink-500 hover:text-pink-700"><X size={14} /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <div>
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                    placeholder="Product এর নাম লিখে খুঁজুন..."
                                    className={`${inputCls} pl-9`}
                                />
                            </div>
                            {productSearch.trim() && (
                                <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                                    {searchResults.length === 0 ? (
                                        <p className="p-3 text-sm text-gray-500">{allProducts.length === 0 ? 'Product লোড হচ্ছে...' : 'কোনো product পাওয়া যায়নি'}</p>
                                    ) : searchResults.map((pr) => (
                                        <button
                                            type="button"
                                            key={pr.productId}
                                            onClick={() => { addPreferred(pr); setProductSearch(''); }}
                                            className="w-full flex items-center gap-3 p-2 text-left hover:bg-orange-50"
                                        >
                                            {pr.image && <img src={pr.image} alt="" className="w-9 h-9 rounded object-cover" onError={(e) => { e.target.style.visibility = 'hidden'; }} />}
                                            <span className="flex-1 min-w-0 text-sm text-gray-900 truncate">{pr.name}</span>
                                            <span className="text-xs text-gray-500">৳{pr.price} · স্টক {pr.stock}</span>
                                            <Plus size={16} className="text-orange-500" />
                                        </button>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-gray-500 mt-1.5">এখানে রাখা product গুলো এই কাস্টমারের সব order এ (Delivered / Return / Cancel) একই থাকে।</p>
                        </div>
                    </Section>

                    {/* Repeat order */}
                    <Section icon={BellRing} title="Repeat Order Reminder">
                        <button
                            type="button"
                            onClick={() => setRepeatOn(!repeatOn)}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition
                                ${repeatOn ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                        >
                            <CalendarClock size={20} className={repeatOn ? 'text-orange-500' : 'text-gray-400'} />
                            <span className="flex-1 text-sm font-semibold text-gray-900">কাস্টমার আবার order করতে চান</span>
                            <span className={`text-xs px-2 py-1 rounded-full font-semibold ${repeatOn ? 'bg-orange-400 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                {repeatOn ? 'ON' : 'OFF'}
                            </span>
                        </button>

                        {repeatOn && (
                            <>
                                {r0?.status === 'done' && r0?.date === repeatDate && (
                                    <div className="p-2.5 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg">
                                        এই reminder এর order হয়ে গেছে ✓ — নতুন তারিখ দিলে আবার Pending হবে।
                                    </div>
                                )}
                                <div>
                                    <label className={labelCls}>কবে order করবেন (Reminder date)</label>
                                    <input type="date" value={repeatDate} onChange={(e) => setRepeatDate(e.target.value)} className={inputCls} />
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {QUICK_DATES.map((q) => (
                                            <button
                                                type="button"
                                                key={q.label}
                                                onClick={() => setRepeatDate(q.get())}
                                                className="px-2.5 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-700 hover:bg-orange-200"
                                            >
                                                +{q.label}
                                            </button>
                                        ))}
                                    </div>
                                    {repeatDate && (() => {
                                        const d = daysFromToday(repeatDate);
                                        return (
                                            <p className="text-xs text-gray-500 mt-1.5">
                                                {fmtYMD(repeatDate)} — {d === 0 ? 'আজই' : d > 0 ? `${d} দিন পরে` : `${Math.abs(d)} দিন আগের তারিখ`}
                                            </p>
                                        );
                                    })()}
                                </div>
                                <div>
                                    <label className={labelCls}>কোন product আবার নিবেন</label>
                                    <input value={repeatProduct} onChange={(e) => setRepeatProduct(e.target.value)} className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Reminder নোট</label>
                                    <input value={repeatNote} onChange={(e) => setRepeatNote(e.target.value)} placeholder="যেমন: বেতন পেলে নিবেন, ২টা নিবেন..." className={inputCls} />
                                </div>
                            </>
                        )}
                    </Section>
                </div>

                {/* Footer */}
                <div className="p-6 pt-3 flex-shrink-0">
                    {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                            onClick={() => save()}
                            disabled={busy}
                            className="flex items-center justify-center gap-2 bg-orange-400 hover:bg-orange-500 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save size={20} />
                            {busy ? 'Saving...' : 'Save Profile'}
                        </button>
                        <button
                            onClick={() => save({ create: true })}
                            disabled={busy}
                            className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ShoppingCart size={20} />
                            Save &amp; Order Create
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
